import { useEffect, useState } from "react"
import type { LandingLocale } from "./landingCopy"

const STORAGE_KEY = "viridian.landing.locale"

function readStoredLocale(): LandingLocale | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw === "de" || raw === "en" ? raw : null
  } catch {
    return null
  }
}

function storeLocale(locale: LandingLocale) {
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    // ignore quota / private mode
  }
}

function localeFromNavigator(): LandingLocale {
  const lang = (navigator.language || "en").toLowerCase()
  return lang.startsWith("de") ? "de" : "en"
}

async function localeFromIp(): Promise<LandingLocale | null> {
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), 4000)
  try {
    const res = await fetch("https://ipapi.co/country_code/", { signal: controller.signal })
    if (!res.ok) return null
    const code = (await res.text()).trim().toUpperCase()
    if (code === "DE") return "de"
    return "en"
  } catch {
    return null
  } finally {
    window.clearTimeout(timer)
  }
}

export function useLandingLocale() {
  const [ locale, setLocale ] = useState<LandingLocale>(() => readStoredLocale() ?? "en")
  const [ ready, setReady ] = useState(() => readStoredLocale() != null)

  useEffect(() => {
    const stored = readStoredLocale()
    if (stored) {
      setLocale(stored)
      setReady(true)
      return
    }

    let cancelled = false
    localeFromIp()
      .then(detected => {
        if (cancelled) return
        setLocale(detected ?? localeFromNavigator())
        setReady(true)
      })
      .catch(() => {
        if (!cancelled) {
          setLocale(localeFromNavigator())
          setReady(true)
        }
      })

    return () => {
      cancelled = true
    }
  }, [])

  const setLandingLocale = (next: LandingLocale) => {
    storeLocale(next)
    setLocale(next)
    setReady(true)
  }

  return { locale, setLandingLocale, ready }
}
